import { useCallback, useEffect, useRef, useState } from 'react';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button, cn } from '@alamo/ui';

import { isZapsignSigningUrl } from './signatureProvider';

export const ZAPSIGN_EMBED_EVENTS = [
  'zs-doc-loaded',
  'zs-doc-signed',
  'zs-signed-file-ready',
] as const;
export type ZapsignEmbedEvent = (typeof ZAPSIGN_EMBED_EVENTS)[number];

const ZAPSIGN_LOAD_TIMEOUT_MS = 20_000;
type LoadState = 'loading' | 'slow' | 'ready' | 'error' | 'confirming';

function isZapsignEmbedEvent(value: unknown): value is ZapsignEmbedEvent {
  return typeof value === 'string' && (ZAPSIGN_EMBED_EVENTS as readonly string[]).includes(value);
}

export interface ZapsignEmbedFrameProps {
  envelopeUrl: string;
  /** Requests server reconciliation; provider messages never establish authorization. */
  onSigned: () => void;
  onEvent?: (event: ZapsignEmbedEvent) => void;
  /** Refreshes unusable signing metadata through the owning flow. */
  onRetryUnavailable?: () => void;
  loadTimeoutMs?: number;
  fillHeight?: boolean;
}

export function ZapsignEmbedFrame({
  envelopeUrl,
  onSigned,
  onEvent,
  onRetryUnavailable,
  loadTimeoutMs = ZAPSIGN_LOAD_TIMEOUT_MS,
  fillHeight = false,
}: ZapsignEmbedFrameProps) {
  const { t } = useTranslation('signature');
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  if (!isZapsignSigningUrl(envelopeUrl)) {
    return (
      <div
        role="alert"
        data-testid="zapsign-error"
        className="space-y-3 rounded-lg border border-erro-apoio/20 bg-erro-apoio/5 p-3 font-body text-sm text-erro-apoio"
      >
        <p>{t('messages.iframe_error')}</p>
        <Button
          variant="secondary"
          type="button"
          onClick={onRetryUnavailable}
          data-testid="zapsign-retry"
        >
          {t('actions.refresh')}
        </Button>
      </div>
    );
  }
  return (
    <ZapsignEmbedAttempt
      key={`${envelopeUrl}:${attempt}`}
      envelopeUrl={envelopeUrl}
      onSigned={onSigned}
      onEvent={onEvent}
      loadTimeoutMs={loadTimeoutMs}
      fillHeight={fillHeight}
      onRetry={retry}
    />
  );
}

function ZapsignEmbedAttempt({
  envelopeUrl,
  onSigned,
  onEvent,
  loadTimeoutMs,
  fillHeight,
  onRetry,
}: Omit<ZapsignEmbedFrameProps, 'onRetryUnavailable'> & {
  loadTimeoutMs: number;
  fillHeight: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation('signature');
  const frame = useRef<HTMLIFrameElement>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [htmlLoaded, setHtmlLoaded] = useState(false);
  const onSignedRef = useRef(onSigned);
  onSignedRef.current = onSigned;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const expectedOrigin = new URL(envelopeUrl).origin;

  useEffect(() => {
    const processed = new Set<ZapsignEmbedEvent>();
    let settled = false;
    let reconciliationRequested = false;
    const watchdog = setTimeout(() => {
      if (!settled) setState((current) => (current === 'loading' ? 'slow' : current));
    }, loadTimeoutMs);
    const element = frame.current;
    function handleFrameError() {
      settled = true;
      clearTimeout(watchdog);
      setState('error');
    }
    function handleMessage(event: MessageEvent) {
      if (event.origin !== expectedOrigin || event.source !== frame.current?.contentWindow) return;
      if (!isZapsignEmbedEvent(event.data) || processed.has(event.data)) return;
      processed.add(event.data);
      settled = true;
      clearTimeout(watchdog);
      if (event.data === 'zs-doc-loaded') {
        setState((current) => (current === 'confirming' ? current : 'ready'));
      } else {
        setState('confirming');
        if (!reconciliationRequested) {
          reconciliationRequested = true;
          onSignedRef.current();
        }
      }
      onEventRef.current?.(event.data);
    }
    window.addEventListener('message', handleMessage);
    element?.addEventListener('error', handleFrameError);
    return () => {
      clearTimeout(watchdog);
      window.removeEventListener('message', handleMessage);
      element?.removeEventListener('error', handleFrameError);
    };
  }, [expectedOrigin, loadTimeoutMs]);

  const recoverable = state === 'slow' || state === 'error';
  return (
    <div
      className={cn(
        'relative w-full',
        fillHeight && 'flex min-h-0 flex-1 flex-col md:block md:flex-none',
      )}
      data-testid="zapsign-embed-frame"
    >
      {recoverable && (
        <div
          role="status"
          data-testid={state === 'error' ? 'zapsign-error' : 'zapsign-slow'}
          className="mb-3 shrink-0 space-y-3 rounded-lg border border-laranja-encanto/30 bg-laranja-encanto-04 p-3 font-body text-sm text-ink"
        >
          <p>{t(state === 'error' ? 'messages.iframe_error' : 'messages.iframe_slow')}</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" type="button" onClick={onRetry} data-testid="zapsign-retry">
              {t('actions.reload_document')}
            </Button>
            <Button asChild variant="outline">
              <a
                href={envelopeUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="zapsign-open-external"
              >
                {t('actions.open_external')}
              </a>
            </Button>
          </div>
        </div>
      )}
      {state === 'loading' && !htmlLoaded && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-alamo-surface"
          data-testid="zapsign-loading"
        >
          <div className="flex flex-col items-center gap-2 font-body text-sm text-ink/50">
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
            <p>{t('messages.iframe_loading')}</p>
          </div>
        </div>
      )}
      {/* A warning never replaces the document; explicit retry or a new URL starts another attempt. */}
      <iframe
        ref={frame}
        src={envelopeUrl}
        title={t('zapsign.frame_title')}
        allow="camera; geolocation"
        onLoad={() => setHtmlLoaded(true)}
        className={cn(
          'w-full border-0',
          fillHeight
            ? 'min-h-0 flex-1 rounded-none md:h-[680px] md:max-h-[78dvh] md:flex-none md:rounded-lg'
            : 'h-[680px] max-h-[78dvh] rounded-lg',
        )}
        data-testid="zapsign-iframe"
      />
      <p
        className={cn(
          'mt-2 shrink-0 text-center font-body text-xs text-ink/60',
          fillHeight && 'px-3 pb-1 md:px-0 md:pb-0',
        )}
        data-testid={state === 'confirming' ? 'zapsign-signed-processing' : 'zapsign-otp-hint'}
      >
        {t(state === 'confirming' ? 'zapsign.confirming' : 'zapsign.otp_hint')}
      </p>
    </div>
  );
}
